import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import translations from '../data/translations';
import { motion } from 'framer-motion';

export default function AboutSection() {
  const { lang } = useLanguage();
  const t = translations.about[lang];

  return (
    <section id="about" data-testid="about-section" className="relative py-24 md:py-32 bg-enbi-offwhite overflow-hidden">
      {/* Subtle diagonal accent */}
      <div
        className="absolute top-0 right-0 w-[30%] h-full bg-enbi-navy/[0.03]"
        style={{ clipPath: 'polygon(50% 0, 100% 0, 100% 100%, 0% 100%)' }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left: Image with diagonal clip */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div
                className="overflow-hidden"
                style={{ clipPath: 'polygon(0 0, 100% 0, 100% 85%, 0 100%)' }}
              >
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                  alt="ENBI Marketing Team"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              {/* Gold accent block */}
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-enbi-gold/20" />
              <div className="absolute -bottom-2 -left-2 w-24 h-24 border-2 border-enbi-gold" />
            </div>
          </motion.div>

          {/* Right: Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-[2px] bg-enbi-gold" />
              <span className="text-enbi-gold text-xs tracking-[0.3em] uppercase font-semibold">
                {t.label}
              </span>
            </div>

            <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-semibold text-enbi-navy uppercase tracking-tight mb-8">
              {t.headline}
            </h2>

            <p className="text-slate-600 text-base leading-relaxed mb-6">
              {t.p1}
            </p>
            <p className="text-slate-600 text-base leading-relaxed mb-10">
              {t.p2}
            </p>

            {/* Mission box */}
            <div className="relative bg-enbi-navy p-8" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 95%)' }}>
              <div className="absolute top-0 left-0 w-1 h-full bg-enbi-gold" />
              <h3 className="font-outfit text-enbi-gold text-sm tracking-[0.2em] uppercase font-semibold mb-3">
                {t.mission}
              </h3>
              <p className="text-white/80 text-sm leading-relaxed">
                {t.missionText}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
